import { ErrorCode } from 'react-dropzone'

import type { Connector, ConnectorContent, ConnectorWithBrain } from './connector'
import type { Folder } from './folder'
import type { Vector } from './vector'

const maxSize = 250 * 1024 * 1024

export const errorMessages = {
	[ErrorCode.FileInvalidType]: 'File type not accepted, sorry!',
	[ErrorCode.FileTooLarge]: `File is too big, max size is ${maxSize / 1024 / 1024} Mb`,
	[ErrorCode.FileTooSmall]: 'File is too small',
	[ErrorCode.TooManyFiles]: 'Too many files'
}

export const errorCodes: Record<ErrorCode[number], ContentUploadErrorCode> = {
	[ErrorCode.FileInvalidType]: 'content-type-not-supported',
	[ErrorCode.FileTooLarge]: 'file-too-big',
	[ErrorCode.FileTooSmall]: 'empty-content',
	[ErrorCode.TooManyFiles]: 'internal-error'
}

export const contentType = [
	'image/png',
	'image/jpeg',
	'text/html',
	'text/plain',
	'text/markdown',
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'audio/mpeg',
	'audio/mp4',
	'audio/x-m4a'
] as const

export type ContentType = typeof contentType[number]

export const contentUploadStatus = ['waiting', 'processing', 'processed', 'failed'] as const

export type ContentUploadStatus = typeof contentUploadStatus[number]

export const contentSource = ['web', 'upload', 'youtube'] as const

export type ContentSource = typeof contentSource[number]

export const contentUploadErrorCode = [
	'brain-is-full',
	'connection-error',
	'content-corrupt',
	'content-type-not-supported',
	'empty-content',
	'file-too-big',
	'internal-error',
	'invalid-file-type',
	'invalid-url',
	'invalid-youtube-link',
	'parsing-error',
	'protected-content',
	'summary-generation-error',
	'timed-out',
	'video-no-transcript',
	'wrong-password',
	'wrong-response',
	'content-was-deleted',
	'file-not-changed'
] as const

export type ContentUploadErrorCode = typeof contentUploadErrorCode[number]

export type IUploadContentError = {
	error: {
		message: string;
		name: ContentUploadErrorCode;
		data?: Record<string, unknown> | undefined;
	};
}

export interface AddContentError {
	name: ContentUploadErrorCode;
	message: string;
	data?: {
		contentType?: string | undefined;
		statusError?: string | undefined;
	} | undefined;
}

export interface ContentWebMetadata {
	awsKey?: string | undefined;
}

export interface ContentUploadMetadata {
	awsKey: string;
	password?: string | undefined;
}

export interface ContentYoutubeMetadata {
	youtubeId: string;
}

export interface ContentMetadataMap {
	web: ContentWebMetadata;
	youtube: ContentYoutubeMetadata;
	upload: ContentUploadMetadata;
}

export type ContentMetadata<T extends ContentSource = ContentSource> = ContentMetadataMap[T]

export type Content = {
	[K in ContentSource]: BaseContent<K>
  }[ContentSource]

interface BaseContent<T extends ContentSource = ContentSource> {
	id: string;
	title: string;
	source: T;
	type: ContentType | 'video';
	url: string;
	hash: string | undefined;
	index?: number | undefined;

	displayed: boolean;
	size: number;
	metadata: ContentMetadataMap[T];

	uploadStatus: ContentUploadStatus;
	uploadErrorCode?: ContentUploadErrorCode | undefined;

	connectors?: Connector[] | undefined;
	folders?: Folder[] | undefined;
	connectorContents?: ConnectorContent[] | undefined;

	data?: ContentData | undefined;
	vectors?: Vector[] | undefined;

	createdAt: Date;
	updatedAt: Date;
}

export type ContentWithBrains<T extends ContentSource = ContentSource> = Omit<BaseContent<T>, 'connectors'> & {
	connectors: ConnectorWithBrain[];
}

export type ContentWithBrainsAndVectors<T extends ContentSource = ContentSource> = Omit<BaseContent<T>, 'connectors'> & {
	connectors: ConnectorWithBrain[]; vectors: Vector[];
}

export interface ContentData {
	id: string;
	extracted: string;
}

export interface DocumentTitle {
	id: string;
	brainId?: string | undefined;
	title: string;
	source: ContentSource;
	type: ContentType | 'video' | 'folder' | 'link' | 'file' | 'drive' | 'workspace';
}

export type MandatoryContentFields =
	Pick<Content, 'title' | 'source' | 'type' | 'size' | 'metadata'>
	& Partial<Content>

export function isUploadContent(content: BaseContent): content is BaseContent<'upload'> {
	return content.source === 'upload'
}

export function isYoutubeContent(content: BaseContent): content is BaseContent<'youtube'> {
	return content.source === 'youtube'
}

export function isWebContent(content: BaseContent): content is BaseContent<'web'> {
	return content.source === 'web'
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
export const isValidContentType = (type: string): boolean => contentType.includes(type as any)
